'use client'

/**
 * The company's brand theme for this workspace (spec docs/superpowers/specs/2026-10-29-branding-kit-and-brand-theme-design.md,
 * sections 3.6 and 3.6a): GET /v1/branding/theme/effective, which every signed-in person can read.
 *
 * Mounted once, by the theme scope (components/app-theme/app-theme-scope.tsx, an isolation spec holds that), so it runs
 * on app pages alone and never in the portal, the play area or /admin. The menu in the header sits outside that tree and
 * reads the answer from the store.
 *
 * It reads again when the tab comes back into view and when the window regains focus: a lock an admin has just switched on
 * reaches everyone at their next look. These are two doors. `refetchOnWindowFocus` is only the first: TanStack's focus
 * manager listens to `visibilitychange` alone, which does not fire for a visible window that regains focus (a second
 * monitor, another app in front), so useBrandThemeSync hands the scope a read for its `focus` listener.
 *
 * A failed read leaves `data` as the last good answer, or nothing at all, and only an answer is ever adopted, so one bad
 * request never paints Classic over a working brand. That is for a brand this workspace answered with: a switch reloads nothing, so
 * a read that fails with no answer for THIS workspace would leave the last client's colours and lock on the next client's page, and
 * the stores take those down (noBrandAnswerFor). Errors take the API client's own path (a 401 ends the session, as anywhere else);
 * nothing here shows them, because the page keeps working.
 */
import { useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchApi } from '@/lib/api/client'
import { adoptEffectiveBrandTheme, noBrandAnswerFor } from '@/lib/theme/app-themes/app-theme-store'
import { EFFECTIVE_BRAND_THEME_QUERY_KEY, parseEffectiveBrandTheme, type EffectiveBrandTheme } from '@/lib/theme/app-themes/brand-theme'
import { isViewingAsAnotherUser, useThemeWorkspaceId } from '@/lib/theme/app-themes/use-app-theme'
import { adoptEffectiveIconSet } from '@/lib/theme/icon-sets'

export const EFFECTIVE_BRAND_THEME_PATH = '/branding/theme/effective'

export function useEffectiveBrandTheme() {
  const workspaceId = useThemeWorkspaceId()
  return useQuery<EffectiveBrandTheme | null>({
    queryKey: [...EFFECTIVE_BRAND_THEME_QUERY_KEY, workspaceId],
    enabled: !!workspaceId,
    refetchOnWindowFocus: 'always',
    queryFn: async () => parseEffectiveBrandTheme(await fetchApi<unknown>(EFFECTIVE_BRAND_THEME_PATH)),
  })
}

/**
 * Keep the stores in step with the server's answer. While a super-admin views as someone, nothing stays in this browser.
 * Returns the read for a `focus` listener: nothing with no workspace to ask about, and a read already on its way is joined.
 */
export function useBrandThemeSync(userId: string | null): () => void {
  const workspaceId = useThemeWorkspaceId()
  const { data, status, refetch } = useEffectiveBrandTheme()

  useEffect(() => {
    // No user yet is not a view-as: an owner-less adoption would clear the cookie the first paint needs.
    if (!userId) return
    const owner = isViewingAsAnotherUser() ? null : userId
    if (data) {
      adoptEffectiveBrandTheme(data, owner, workspaceId)
      adoptEffectiveIconSet(data.iconSet, owner)
    } else if (workspaceId && status !== 'pending' && noBrandAnswerFor(workspaceId, owner)) {
      // The read is over and left nothing: failed, or `null`, which is what a non-answer parses to.
      adoptEffectiveIconSet(null, owner)
    }
  }, [data, status, userId, workspaceId])

  return useCallback(() => {
    if (workspaceId) void refetch({ cancelRefetch: false })
  }, [refetch, workspaceId])
}
